import get from 'lodash/get';
import React, { Component, Fragment, createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cnames from 'classnames';
import findIndex from 'lodash/findIndex';
import gql from 'graphql-tag';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { compose } from 'recompose';
import imagesLoaded from 'imagesloaded';
import { useInView } from 'react-intersection-observer';

import * as constants from '@redbubble/design-system/react/constants';
import Button from '@redbubble/design-system/react/Button';
import Box from '@redbubble/design-system/react/Box';

import config from '../../../../config';
import styles from './FeedGrid.css';
import FeedGridItem from '../FeedGridItem';
import Loading from '../../Loading';
import { ErrorWithRetry } from '../../ErrorWithRetry/ErrorWithRetry';
import messages from './messages';
import analyticsPayload from '../../../lib/analytics/analytics';
import {
  withAddWorkToFavorites,
  withRemoveWorkFromFavorites
} from '../../../containers/apollo/withToggleListFavorite';
import SignUpModal from '../../SignUpModal';
import FirstInView from '../../FirstInView';
import { clientError } from '../../../lib/errorReporting';

const DEFAULT_LOCALE = config('defaultLocale');

const FeedGridItemWithActions = compose(
  withAddWorkToFavorites,
  withRemoveWorkFromFavorites
)(FeedGridItem);

const PaginationTrigger = ({ paginate, inProgress }) => {
  const [ref, inView] = useInView({});

  useEffect(() => {
    if (inView && !inProgress) paginate();
  }, [inView]);

  return <div ref={ref} />;
};

class FeedGrid extends Component {
  constructor(props) {
    super(props);

    this.containerRef = createRef();
    this.itemRefs = {};

    this.onLoadMore = this.onLoadMore.bind(this);
    this.logRecommendationEvents = this.logRecommendationEvents.bind(this);
    this.openSignUpModal = this.openSignUpModal.bind(this);
    this.closeSignUpModal = this.closeSignUpModal.bind(this);
    this.setModalImageUrl = this.setModalImageUrl.bind(this);
    this.handleLayoutComplete = this.handleLayoutComplete.bind(this);

    this.state = {
      mounted: false,
      signUpModalOpen: false,
      modalImageUrl: null,
      initialLayoutComplete: false,
      displayable: new Set()
    };
  }

  openSignUpModal() {
    this.setState({ signUpModalOpen: true });
  }

  closeSignUpModal() {
    this.setState({ signUpModalOpen: false });
  }

  setModalImageUrl(modalImageUrl) {
    this.setState({ modalImageUrl });
  }

  logRecommendationEvents(data) {
    const result = get(data, 'data.cannedWorkRecommendations', {});
    const { from, limit, canSize, hasMore } = result;
    const { loadedMore, endOfRecommendations } = analyticsPayload;
    const payload = hasMore ? loadedMore : endOfRecommendations;

    this.props.logEvent({ analytics: payload(from, limit, canSize) });

    return data;
  }

  initMasonry() {
    if (typeof window === 'undefined') return null;

    const { current: container } = this.containerRef || {};

    if (!container) return null;

    // eslint-disable-next-line global-require
    const Masonry = require('masonry-layout');

    const masonry = new Masonry(this.containerRef.current, {
      itemSelector: `.${styles.gridItem}`,
      initLayout: false
    });

    masonry.on('layoutComplete', this.handleLayoutComplete);

    return masonry;
  }

  get masonry() {
    /* eslint-disable no-underscore-dangle */
    if (!this._masonry) this._masonry = this.initMasonry();

    return this._masonry;
    /* eslint-enable no-underscore-dangle */
  }

  componentDidMount() {
    const { items, refetch, hasMore, loading } = this.props;

    const hasItems = Array.isArray(items) && items.length > 0;

    if (!loading && hasItems && hasMore) {
      this.onLoadMore(this.props.limit);
    }

    if (!loading && !hasItems) {
      refetch();
    }

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ mounted: true });
  }

  componentDidUpdate(prevProps) {
    const { items, loading } = this.props;
    const { items: prevItems } = prevProps;
    const { initialLayoutComplete } = this.state;

    const hadItems = Array.isArray(prevItems) && prevItems.length > 0;
    const hasItems = Array.isArray(items) && items.length > 0;

    const itemsChanged =
      hadItems && hasItems && items.length !== prevItems.length;

    if (!loading && hasItems && !initialLayoutComplete) {
      this.handleInitialLayout();
    } else if (itemsChanged) {
      this.handleItemsChanged(prevItems);
    }
  }

  handleLayoutComplete() {
    const { initialLayoutComplete } = this.state;

    if (!initialLayoutComplete) {
      this.setState({ initialLayoutComplete: true });
    }
  }

  displayItems(items) {
    const { displayable } = this.state;

    this.setState({
      displayable: new Set([
        ...displayable,
        ...items.map(({ work }) => work.id)
      ])
    });
  }

  handleInitialLayout() {
    const { items } = this.props;

    imagesLoaded(`.${styles.gridItem}`, () => {
      this.masonry.layout();
      this.displayItems(items);
    });
  }

  handleItemsChanged(prevItems) {
    const { items } = this.props;

    const previousLastItem = prevItems[prevItems.length - 1];
    const firstAppendedIndex =
      findIndex(items, ['work.id', previousLastItem.work.id]) + 1;
    const appendedItems = items.slice(firstAppendedIndex);
    const appendedElements = appendedItems.map(
      ({ work }) => this.itemRefs[work.id]
    );

    imagesLoaded(appendedElements, () => {
      this.masonry.appended(appendedElements);
      this.displayItems(appendedItems);
    });
  }

  onLoadMore(limit) {
    const { fetchMore } = this.props;

    if (typeof fetchMore !== 'function') return Promise.resolve();

    return fetchMore(limit).catch(clientError);
  }

  render() {
    const {
      items,
      loading,
      error,
      refetch,
      analyticsLabel,
      useModal,
      userInfo,
      fetchMore,
      pageSize,
      onContentShowed,
      onLoadingShowed,
      intl,
      fetchingMore,
      hasMore,
      autoPaginate
    } = this.props;

    const {
      modalImageUrl,
      signUpModalOpen,
      initialLayoutComplete,
      displayable,
      mounted
    } = this.state;

    const attemptFeedRender = !loading && mounted;
    const renderError = attemptFeedRender && (!!error || items.length === 0);

    const classNames = cnames(styles.grid, styles.feedLayout, {
      [styles.hidden]: !initialLayoutComplete
    });

    const showButton =
      attemptFeedRender &&
      initialLayoutComplete &&
      !!fetchMore &&
      hasMore &&
      (fetchingMore || !autoPaginate); // When autoPaginate is true, we only show loading state of button.

    return (
      <Fragment>
        {!attemptFeedRender && (
          <Fragment>
            <FirstInView key="loading-showed" onFirstView={onLoadingShowed} />
            <Loading />
          </Fragment>
        )}

        <div className={classNames} ref={this.containerRef}>
          {attemptFeedRender && (
            <FirstInView key="content-showed" onFirstView={onContentShowed} />
          )}

          {attemptFeedRender &&
            items.map(({ work }, index) => (
              <div
                key={work.id}
                className={cnames(styles.gridItem, {
                  [styles.hidden]: !displayable.has(work.id)
                })}
                ref={node => {
                  this.itemRefs[work.id] = node;
                }}
              >
                <FeedGridItemWithActions
                  useModal={useModal}
                  work={work}
                  analyticsLabel={analyticsLabel}
                  index={index}
                  locale={userInfo.locale}
                  openSignUpModal={this.openSignUpModal}
                  setModalImageUrl={this.setModalImageUrl}
                  isLoggedIn={get(userInfo, 'isLoggedIn', false)}
                  loadImage={displayable.has(work.id)}
                />
              </div>
            ))}
        </div>

        {renderError && (
          <ErrorWithRetry
            message={intl.formatMessage(messages.ErrorMessage)}
            retry={refetch}
          />
        )}

        {autoPaginate &&
          attemptFeedRender &&
          initialLayoutComplete &&
          hasMore && (
            <PaginationTrigger
              paginate={() =>
                this.onLoadMore(pageSize).then(this.logRecommendationEvents)
              }
              inProgress={fetchingMore}
            />
          )}

        {showButton && (
          <Box display="flex" justifyContent="center">
            <Button
              loading={fetchingMore}
              strong
              intent={constants.PRIMARY}
              size={constants.LARGE}
              onClick={
                !fetchingMore
                  ? () =>
                      this.onLoadMore(pageSize).then(
                        this.logRecommendationEvents
                      )
                  : null
              }
            >
              <FormattedMessage defaultMessage="Show More" />
            </Button>
          </Box>
        )}

        {modalImageUrl && (
          <SignUpModal
            open={signUpModalOpen}
            imgUrl={modalImageUrl}
            onCloseRequested={this.closeSignUpModal}
          />
        )}
      </Fragment>
    );
  }
}

FeedGrid.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.array,
  pageSize: PropTypes.number,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  analyticsLabel: PropTypes.string,
  useModal: PropTypes.bool,
  userInfo: PropTypes.shape({
    locale: PropTypes.string,
    isLoggedIn: PropTypes.bool
  }),
  fetchMore: PropTypes.func,
  onLoadingShowed: PropTypes.func,
  onContentShowed: PropTypes.func,
  intl: intlShape.isRequired,
  hasMore: PropTypes.bool,
  autoPaginate: PropTypes.bool
};

FeedGrid.defaultProps = {
  loading: false,
  useModal: false,
  userInfo: {
    locale: DEFAULT_LOCALE
  },
  fetchMore: null,
  onLoadingShowed: () => {},
  onContentShowed: () => {},
  hasMore: false,
  autoPaginate: false
};

FeedGrid.displayName = 'FeedGrid';

FeedGrid.fragment = gql`
  fragment FeedGrid on Work {
    id
    matureContent
    ...${FeedGridItem.displayName}
  }

  ${FeedGridItem.fragment}
`;

export const fragments = [
  {
    type: 'Work',
    name: FeedGrid.displayName,
    fragment: FeedGrid.fragment
  }
];

const FeedGridWithIntl = injectIntl(FeedGrid);
FeedGridWithIntl.displayName = FeedGrid.displayName;

export { FeedGrid };
export default FeedGridWithIntl;
